import React, { useContext, useEffect, useRef, useState } from "react";
import { Web } from "sip.js";
import { Paper, Typography, IconButton, Box, Slide, Chip, Avatar, Menu, MenuItem } from "@mui/material";
import CallIcon from "@mui/icons-material/Call";
import CallEndIcon from "@mui/icons-material/CallEnd";
import PhoneInTalkIcon from "@mui/icons-material/PhoneInTalk";
import MicOffIcon from "@mui/icons-material/MicOff";
import MicIcon from "@mui/icons-material/Mic";
import PhoneForwardedIcon from "@mui/icons-material/PhoneForwarded";
import { styled } from "@mui/material/styles";

import { toast } from "react-toastify";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import { useSocket } from "../../context/SocketContext";
import toastError from "../../errors/toastError";

// Softphone WebRTC (WhatsApp Calling API via Asterisk).
// Registra o ramal SIP do atendente no navegador e trata ligações entrantes.
// Montado no layout logado; só aparece se o usuário tiver voz habilitada.

const FloatCard = styled(Paper)(({ theme }) => ({
  position: "fixed",
  bottom: 24,
  right: 24,
  zIndex: 3000,
  padding: theme.spacing(2),
  width: 300,
  borderRadius: 16,
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1.5),
  boxShadow: "0 8px 32px rgba(0,0,0,0.25)"
}));

const RoundBtn = styled(IconButton)(({ bg }) => ({
  backgroundColor: bg,
  color: "#fff",
  width: 52,
  height: 52,
  "&:hover": { backgroundColor: bg, filter: "brightness(0.92)" }
}));

const STATE = { IDLE: "idle", INCOMING: "incoming", CALLING: "calling", ONCALL: "oncall" };

// só dígitos (E.164 sem +); o dialplan do Asterisk acrescenta o + p/ a Meta
const cleanNumber = n => String(n || "").replace(/\D/g, "");

const fmt = secs =>
  `${String(Math.floor(secs / 60)).padStart(2, "0")}:${String(secs % 60).padStart(2, "0")}`;

const Softphone = () => {
  const { isAuth } = useContext(AuthContext);
  const { socket } = useSocket();
  const audioRef = useRef(null);
  const suRef = useRef(null);
  const callInfoRef = useRef(null); // { number, direction, answeredAt } p/ registrar no ticket
  const [callState, setCallState] = useState(STATE.IDLE);
  const [registered, setRegistered] = useState(false);
  const [caller, setCaller] = useState("");
  const [callerPic, setCallerPic] = useState(null);
  const [seconds, setSeconds] = useState(0);
  const [muted, setMuted] = useState(false);
  // origem da ligação: { channel: "telefone"|"whatsapp", setor } — o Asterisk
  // marca as ligações de telefone com displayName "Telefone [(Setor)] +55..."
  const [origin, setOrigin] = useState(null);
  // transferência (blind transfer = REFER p/ o ramal do colega)
  const sipDomainRef = useRef("sip.sabesistemas.com.br");
  const [agents, setAgents] = useState([]);
  const [transferAnchor, setTransferAnchor] = useState(null);

  // busca nome/foto do contato pelo número (enriquece o card)
  const lookupContact = num => {
    if (!num) return;
    api
      .get(`/voice/contact-lookup?number=${num}`)
      .then(({ data }) => {
        if (!data?.found) return;
        if (data.name) setCaller(data.name);
        if (data.profilePicUrl) setCallerPic(data.profilePicUrl);
      })
      .catch(() => {});
  };

  // avisa o atendente quando um cliente autoriza/recusa receber ligações
  // (backend emite "callPermission" ao receber o webhook call_permission_reply)
  useEffect(() => {
    if (!socket) return undefined;
    const onPerm = data => {
      const who = data?.name || data?.number || "O cliente";
      if (data?.granted) {
        let extra = data.permanent ? " (permanente)" : "";
        if (!data.permanent && data.expiresAt) {
          extra = ` (até ${new Date(data.expiresAt).toLocaleDateString("pt-BR")})`;
        }
        toast.success(`✅ ${who} autorizou receber ligações${extra}. Já pode ligar!`, {
          autoClose: 8000
        });
      } else {
        toast.info(`${who} recusou receber ligações por agora.`, { autoClose: 6000 });
      }
    };
    socket.on("callPermission", onPerm);
    return () => socket.off("callPermission", onPerm);
  }, [socket]);

  // publica o status "voz online" p/ o botão Ligar do ticket saber se deve aparecer
  useEffect(() => {
    window.__softphoneReady = registered;
    window.dispatchEvent(
      new CustomEvent("softphone:status", { detail: { ready: registered } })
    );
  }, [registered]);

  // ringback (som de "chamando") enquanto o cliente não atende — padrão BR:
  // tom 425 Hz, 1s ligado / 4s desligado. Gerado via Web Audio (sem arquivo).
  useEffect(() => {
    if (callState !== STATE.CALLING) return undefined;
    let ctx;
    let stopped = false;
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      ctx = new AC();
      const beep = () => {
        if (stopped || !ctx) return;
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sine";
        osc.frequency.value = 425;
        gain.gain.value = 0.12;
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start();
        osc.stop(ctx.currentTime + 1); // 1s de tom
      };
      beep();
      const iv = setInterval(beep, 5000); // repete a cada 5s (1s on + 4s off)
      return () => {
        stopped = true;
        clearInterval(iv);
        try { ctx.close(); } catch (e) { /* noop */ }
      };
    } catch (e) {
      return undefined;
    }
  }, [callState]);

  // toque de ligação ENTRANTE (telefone ou WhatsApp) enquanto ninguém atende:
  // "trim-trim" (2 toques curtos a cada 3s), gerado via Web Audio (sem arquivo).
  // Toca também com a aba em segundo plano.
  useEffect(() => {
    if (callState !== STATE.INCOMING) return undefined;
    let ctx;
    let stopped = false;
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      ctx = new AC();
      if (ctx.state === "suspended") ctx.resume().catch(() => {});
      const tone = (start, dur) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sine";
        osc.frequency.value = 480;
        gain.gain.setValueAtTime(0.25, start);
        gain.gain.setValueAtTime(0, start + dur);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(start);
        osc.stop(start + dur);
      };
      const ring = () => {
        if (stopped || !ctx) return;
        const t = ctx.currentTime;
        tone(t, 0.4);
        tone(t + 0.6, 0.4);
      };
      ring();
      const iv = setInterval(ring, 3000);
      return () => {
        stopped = true;
        clearInterval(iv);
        try { ctx.close(); } catch (e) { /* noop */ }
      };
    } catch (e) {
      return undefined;
    }
  }, [callState]);

  // ligação entrante com a aba em segundo plano: pisca o título da aba
  // e mostra notificação do sistema (se o navegador já tiver permissão).
  useEffect(() => {
    if (callState !== STATE.INCOMING) return undefined;
    const original = document.title;
    const label = `📞 Ligação de ${caller || "cliente"}`;
    let on = false;
    const iv = setInterval(() => {
      on = !on;
      document.title = on ? label : original;
    }, 1000);
    let notif = null;
    try {
      if (document.hidden && "Notification" in window && Notification.permission === "granted") {
        const via = origin?.channel === "telefone" ? "por telefone" : "pelo WhatsApp";
        notif = new Notification(`Ligação recebida ${via}`, {
          body: [caller, origin?.setor].filter(Boolean).join(" · "),
          requireInteraction: true
        });
        notif.onclick = () => { window.focus(); notif.close(); };
      }
    } catch (e) { /* noop */ }
    return () => {
      clearInterval(iv);
      document.title = original;
      try { if (notif) notif.close(); } catch (e) { /* noop */ }
    };
  }, [callState, caller, origin]);

  // pede permissão de notificação uma vez, quando o ramal de voz registra
  useEffect(() => {
    if (!registered) return;
    try {
      if ("Notification" in window && Notification.permission === "default") {
        Notification.requestPermission().catch(() => {});
      }
    } catch (e) { /* noop */ }
  }, [registered]);

  // cronômetro da chamada
  useEffect(() => {
    if (callState !== STATE.ONCALL) return undefined;
    setSeconds(0);
    const t = setInterval(() => setSeconds(s => s + 1), 1000);
    return () => clearInterval(t);
  }, [callState]);

  useEffect(() => {
    if (!isAuth) return undefined;
    let cancelled = false;
    let simpleUser = null;

    const start = async () => {
      let creds;
      try {
        const { data } = await api.get("/voice/credentials");
        if (!data || !data.enabled) return; // atendente sem voz habilitada
        creds = data;
        if (data.sipDomain) sipDomainRef.current = data.sipDomain;
      } catch (err) {
        return; // silencioso: voz é opcional
      }
      if (cancelled) return;

      try {
        const options = {
          aor: creds.sipUri,
          media: {
            constraints: { audio: true, video: false },
            remote: { audio: audioRef.current }
          },
          userAgentOptions: {
            authorizationUsername: creds.username,
            authorizationPassword: creds.password,
            displayName: creds.displayName || creds.username,
            transportOptions: { server: creds.wssServer },
            sessionDescriptionHandlerFactoryOptions: {
              peerConnectionConfiguration: { iceServers: creds.iceServers || [] },
              // o sip.js só responde (200 OK) quando a coleta ICE termina ou dá o
              // timeout (padrão 5s) — em PC com VPN/adaptador virtual ela quase nunca
              // "termina", e o atendente ficava ~4s mudo ao atender. host+srflx
              // (STUN) chegam em <200ms; o Asterisk tem IP público e rtp_symmetric.
              iceGatheringTimeout: 600
            }
          }
        };

        simpleUser = new Web.SimpleUser(creds.wssServer, options);
        suRef.current = simpleUser;

        simpleUser.delegate = {
          onRegistered: () => !cancelled && setRegistered(true),
          onUnregistered: () => !cancelled && setRegistered(false),
          onServerDisconnect: () => !cancelled && setRegistered(false),
          onCallReceived: async () => {
            if (cancelled) return;
            const id = simpleUser?.session?.remoteIdentity?.uri?.user || "";
            const digits = cleanNumber(id);
            const dn = simpleUser?.session?.remoteIdentity?.displayName || "";
            const channel = /^telefone/i.test(dn) ? "telefone" : "whatsapp";
            const setor = (/\(([^)]+)\)/.exec(dn) || [])[1] || null;
            setOrigin({ channel, setor });
            callInfoRef.current = { number: digits, direction: "inbound", answeredAt: null, channel };
            setCaller(id.replace(/^\+?55/, "") || id || "Desconhecido");
            setCallerPic(null);
            lookupContact(digits); // nome/foto do contato
            setCallState(STATE.INCOMING);
          },
          onCallAnswered: () => {
            if (cancelled) return;
            if (callInfoRef.current) callInfoRef.current.answeredAt = Date.now();
            setCallState(STATE.ONCALL);
          },
          onCallHangup: () => {
            if (cancelled) return;
            const info = callInfoRef.current;
            // registra: chamada ATENDIDA (duração) ou chamada RECEBIDA perdida.
            // Saída não-atendida NÃO registra aqui (recusa/sem-permissão são tratadas no onReject),
            // senão duplicaria com "recusada pelo cliente".
            const answered = !!(info && info.answeredAt);
            if (info && info.number && (answered || info.direction === "inbound")) {
              const durationSec = answered
                ? Math.round((Date.now() - info.answeredAt) / 1000)
                : 0;
              api
                .post("/voice/log-call", {
                  number: info.number,
                  direction: info.direction,
                  channel: info.channel || "whatsapp",
                  durationSec,
                  startedAt: info.answeredAt || Date.now()
                })
                .catch(() => {});
            }
            callInfoRef.current = null;
            setCallState(STATE.IDLE);
            setCaller("");
            setCallerPic(null);
            setOrigin(null);
            setMuted(false);
          }
        };

        await simpleUser.connect();
        await simpleUser.register();
      } catch (err) {
        toastError(err);
      }
    };

    start();

    return () => {
      cancelled = true;
      const su = suRef.current;
      if (su) {
        try {
          su.unregister().catch(() => {});
          su.disconnect().catch(() => {});
        } catch (e) {
          /* noop */
        }
      }
      suRef.current = null;
    };
  }, [isAuth]);

  const handleAnswer = async () => {
    const su = suRef.current;
    if (!su || callState !== STATE.INCOMING) return; // ignora cliques repetidos
    // feedback imediato: troca o botão "atender" por "em ligação" (o answer()
    // leva um instante preparando microfone + mídia; sem isto o usuário clica de novo).
    setCallState(STATE.ONCALL);
    try {
      await su.answer();
    } catch (err) {
      // "Invalid session state" = já está atendendo (clique duplo); ignorar.
      // eslint-disable-next-line no-console
      console.error("[Softphone] answer:", err);
    }
  };

  const handleHangup = async () => {
    const info = callInfoRef.current;
    // atendente desistiu antes do cliente atender (ligação de saída "Chamando…")
    if (callState === STATE.CALLING && info && info.number && !info.answeredAt) {
      info.outcome = "canceled"; // sinaliza p/ o onCallHangup não duplicar
      api
        .post("/voice/log-call", {
          number: info.number,
          direction: "outbound",
          channel: info.channel || "whatsapp",
          durationSec: 0,
          outcome: "canceled"
        })
        .catch(() => {});
      toast.info("Ligação cancelada — o cliente não chegou a atender.", { autoClose: 4000 });
    }
    try {
      const su = suRef.current;
      if (callState === STATE.INCOMING) await su?.decline();
      else await su?.hangup();
    } catch (err) {
      /* noop */
    }
    setCallState(STATE.IDLE);
  };

  // abre a lista de colegas com ramal (busca na hora: pega quem entrou/saiu)
  const openTransfer = async ev => {
    const anchor = ev.currentTarget;
    try {
      const { data } = await api.get("/voice/agents");
      setAgents(Array.isArray(data) ? data : []);
    } catch (err) {
      setAgents([]);
    }
    setTransferAnchor(anchor);
  };

  // transferência às cegas: o Asterisk leva o cliente p/ o ramal do colega e
  // encerra esta perna (cai no onCallHangup, que registra a ligação no ticket).
  // Se o colega não atender, ligação de telefone vai p/ o recado.
  const handleTransfer = async agent => {
    setTransferAnchor(null);
    const su = suRef.current;
    if (!su || !su.session || callState !== STATE.ONCALL) return;
    try {
      await su.sessionManager.transfer(
        su.session,
        `sip:${agent.sipUsername}@${sipDomainRef.current}`,
        {
          requestDelegate: {
            onAccept: () => toast.info(`Ligação transferida para ${agent.name}.`),
            onReject: () => toastError("A transferência foi recusada pelo servidor de voz.")
          }
        }
      );
    } catch (err) {
      toastError(err);
    }
  };

  const toggleMute = () => {
    const su = suRef.current;
    if (!su) return;
    if (muted) su.unmute();
    else su.mute();
    setMuted(!muted);
  };

  // originar (outbound): o botão "Ligar" do ticket dispara o evento global.
  useEffect(() => {
    const onCallRequest = async e => {
      const su = suRef.current;
      const num = cleanNumber(e?.detail?.number);
      if (!su || !num) return;
      if (callState !== STATE.IDLE) {
        toastError("Já existe uma ligação em andamento.");
        return;
      }
      const domain = e?.detail?.sipDomain || "sip.sabesistemas.com.br";
      const whatsappId = e?.detail?.whatsappId;
      const ticketId = e?.detail?.ticketId;
      // via "telefone": sai pelo tronco da operadora (Asterisk [atendentes] _tel.),
      // sem consentimento da Meta; o padrão segue sendo voz do WhatsApp.
      const porTelefone = e?.detail?.via === "telefone";
      const channel = porTelefone ? "telefone" : "whatsapp";
      setCaller(e?.detail?.name || num);
      setCallerPic(null);
      lookupContact(num); // foto/nome do contato
      callInfoRef.current = { number: num, direction: "outbound", answeredAt: null, channel };
      setOrigin({ channel, setor: null });
      setCallState(STATE.CALLING);
      try {
        // 3º arg = inviterInviteOptions (é onde o requestDelegate é lido;
        // no 2º arg ele é ignorado — assinatura call(dest, inviterOptions, inviteOptions)).
        await su.call(`sip:${porTelefone ? "tel" : ""}${num}@${domain}`, undefined, {
          // detecta a recusa da Meta por falta de consentimento (403) e, nesse
          // caso, envia o pedido de permissão automaticamente.
          requestDelegate: {
            onReject: response => {
              const code = response?.message?.statusCode;
              const reason = response?.message?.reasonPhrase || "";
              // eslint-disable-next-line no-console
              console.warn("[Softphone] ligação recusada:", code, reason);
              // 403 é ambíguo na Meta: pode ser "sem permissão" OU "cliente recusou a chamada".
              // Distinguimos pelo pedido de permissão: se a Meta ACEITA enviar o pedido, era
              // falta de permissão; se RECUSA (400), o cliente já tinha permissão => foi recusa.
              if (porTelefone) {
                if (code === 486 || code === 600) toast.warn("Linha ocupada.", { autoClose: 5000 });
                else if (code === 484 || code === 404) toast.warn("Número de telefone inválido.", { autoClose: 5000 });
                else if (code && code >= 400 && code !== 487)
                  toast.warn(`A ligação por telefone não completou (${code}).`, { autoClose: 6000 });
              } else if (code === 403 && whatsappId) {
                // O backend confere a permissão na Meta antes de pedir: se o cliente já
                // autorizou, a recusa foi da chamada; senão envia o pedido (mensagem livre
                // na janela de 24 h, modelo aprovado fora dela). Sapem 31/10/2026.
                api
                  .post(`/waba/${whatsappId}/call-permission`, { to: num, ticketId })
                  .then(({ data }) => {
                    if (data?.data?.status === "autorizado") {
                      toast.warn(
                        "A ligação não foi atendida — o cliente recusou a chamada ou está indisponível.",
                        { autoClose: 7000 }
                      );
                      api
                        .post("/voice/log-call", {
                          number: num,
                          direction: "outbound",
                          durationSec: 0,
                          outcome: "declined"
                        })
                        .catch(() => {});
                      return;
                    }
                    toast.info(
                      "O cliente ainda não autorizou ligações. Enviamos o pedido de permissão — assim que ele aceitar, é só ligar de novo.",
                      { autoClose: 8000 }
                    );
                  })
                  .catch(err => {
                    toast.warn(
                      err?.response?.data?.error ||
                        "Não foi possível pedir a autorização de ligação ao cliente agora.",
                      { autoClose: 9000 }
                    );
                  });
              } else if (code === 487) {
                // 487 = Request Terminated: cancelamento local (atendente desistiu),
                // já tratado no handleHangup — não avisa de novo.
              } else if (code && code >= 400) {
                toast.warn(`Não foi possível completar a ligação (${code}).`);
              }
            }
          }
        });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("[Softphone] call:", err);
        setCallState(STATE.IDLE);
        toastError(err);
      }
    };
    window.addEventListener("softphone:call", onCallRequest);
    return () => window.removeEventListener("softphone:call", onCallRequest);
  }, [callState]);

  return (
    <>
      {/* elemento de áudio remoto (sempre montado) */}
      <audio ref={audioRef} autoPlay />

      <Slide direction="up" in={callState !== STATE.IDLE} mountOnEnter unmountOnExit>
        <FloatCard elevation={8}>
          <Box display="flex" alignItems="center" gap={1}>
            <PhoneInTalkIcon color="primary" />
            <Typography variant="subtitle1" fontWeight={600}>
              {callState === STATE.INCOMING
                ? "Ligação recebida"
                : callState === STATE.CALLING
                ? "Chamando…"
                : "Em ligação"}
            </Typography>
          </Box>

          <Box display="flex" alignItems="center" gap={1.5}>
            <Avatar src={callerPic || undefined} sx={{ width: 44, height: 44 }}>
              {(caller || "?").charAt(0).toUpperCase()}
            </Avatar>
            <Typography variant="h6" sx={{ wordBreak: "break-word" }}>
              {caller || "WhatsApp"}
            </Typography>
          </Box>

          {origin && (
            <Box display="flex" gap={1} flexWrap="wrap">
              <Chip
                size="small"
                color={origin.channel === "telefone" ? "primary" : "success"}
                label={origin.channel === "telefone" ? "Telefone" : "WhatsApp"}
              />
              {origin.setor && <Chip size="small" variant="outlined" label={origin.setor} />}
            </Box>
          )}

          {callState === STATE.ONCALL && (
            <Typography variant="body2" color="textSecondary">
              {fmt(seconds)}
            </Typography>
          )}

          <Box display="flex" justifyContent="center" gap={3} mt={1}>
            {callState === STATE.INCOMING && (
              <RoundBtn bg="#2e7d32" onClick={handleAnswer} title="Atender">
                <CallIcon />
              </RoundBtn>
            )}
            {callState === STATE.ONCALL && (
              <RoundBtn bg={muted ? "#616161" : "#1976d2"} onClick={toggleMute} title="Mudo">
                {muted ? <MicOffIcon /> : <MicIcon />}
              </RoundBtn>
            )}
            {callState === STATE.ONCALL && callInfoRef.current?.direction === "inbound" && (
              <RoundBtn bg="#6a1b9a" onClick={openTransfer} title="Transferir">
                <PhoneForwardedIcon />
              </RoundBtn>
            )}
            <RoundBtn bg="#c62828" onClick={handleHangup} title="Desligar">
              <CallEndIcon />
            </RoundBtn>
          </Box>
        </FloatCard>
      </Slide>

      <Menu
        anchorEl={transferAnchor}
        open={Boolean(transferAnchor)}
        onClose={() => setTransferAnchor(null)}
        style={{ zIndex: 3100 }}
      >
        {agents.length === 0 && <MenuItem disabled>Nenhum colega com ramal de voz</MenuItem>}
        {agents.map(a => (
          <MenuItem key={a.id} onClick={() => handleTransfer(a)}>
            Transferir para {a.name}
          </MenuItem>
        ))}
      </Menu>

      {/* selo discreto de status do ramal (só quando registrado e ocioso) */}
      {registered && callState === STATE.IDLE && (
        <Chip
          size="small"
          color="success"
          variant="outlined"
          icon={<CallIcon style={{ fontSize: 14 }} />}
          label="Voz online"
          // só informativo: não pode capturar clique de botão que fique embaixo
          style={{ position: "fixed", bottom: 12, right: 12, zIndex: 2000, opacity: 0.7, pointerEvents: "none" }}
        />
      )}
    </>
  );
};

export default Softphone;
